import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Sparkles,
  CalendarDays,
  Camera,
  Wallet,
  ClipboardList,
  FileText,
  LayoutDashboard,
  Check,
  Play,
  Pause,
  ChevronRight,
} from "lucide-react";

const PHOTOS = [
  "https://customer-assets.emergentagent.com/job_tidy-manager-1/artifacts/mltv33cm_IMG_4050.jpeg",
  "https://customer-assets.emergentagent.com/job_tidy-manager-1/artifacts/0lr5qtz9_IMG_4048.jpeg",
  "https://customer-assets.emergentagent.com/job_tidy-manager-1/artifacts/jpmxblgd_IMG_4049.jpeg",
];

const stat = (label, value, sub) => ({ label, value, sub });

/* ---------------- Faux UI panels (on-brand, no real data needed) -------------- */

function DashboardUI() {
  const stats = [
    stat("Today's turnovers", "4", "2 done · 2 scheduled"),
    stat("Outstanding invoices", "$2,340", "3 unpaid"),
    stat("Cleaner payouts (wk)", "$1,120", "6 cleaners"),
    stat("Leads won", "68%", "this month"),
  ];
  return (
    <div className="p-5">
      <p className="text-xs font-semibold tracking-[0.12em] uppercase text-primary mb-3">
        Dashboard
      </p>
      <div className="grid grid-cols-2 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl border border-border bg-card p-3">
            <p className="text-[11px] text-muted-foreground">{s.label}</p>
            <p className="font-display text-2xl font-semibold mt-0.5">{s.value}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-xl border border-border bg-card p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium">Upcoming turnovers</p>
          <span className="text-[10px] text-primary">View all</span>
        </div>
        {["Beach House · 2pm", "Cedar Loft · 4pm", "Pine Cabin · tomorrow"].map((t, i) => (
          <div key={t} className="flex items-center gap-2 py-1.5 text-xs border-t border-border first:border-0">
            <span className={`h-2 w-2 rounded-full ${["bg-primary", "bg-amber-400", "bg-sky-400"][i]}`} />
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}

function CalendarUI() {
  const days = Array.from({ length: 35 }, (_, i) => i - 2);
  const bars = { 6: "Airbnb", 7: "Airbnb", 12: "VRBO", 18: "Airbnb", 19: "Airbnb", 24: "VRBO" };
  return (
    <div className="p-5">
      <p className="text-xs font-semibold tracking-[0.12em] uppercase text-primary mb-3">
        Calendar · Airbnb + VRBO synced
      </p>
      <div className="grid grid-cols-7 gap-1.5">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="text-[10px] text-center text-muted-foreground">{d}</div>
        ))}
        {days.map((d, i) => (
          <div
            key={i}
            className="relative aspect-square rounded-md border border-border bg-card text-[10px] p-1"
          >
            {d > 0 && d <= 30 ? d : ""}
            {bars[i] && (
              <span
                className={`absolute bottom-1 left-1 right-1 h-1.5 rounded-full ${
                  bars[i] === "Airbnb" ? "bg-primary" : "bg-sky-400"
                }`}
              />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-primary" /> Airbnb</span>
        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-sky-400" /> VRBO</span>
        <span className="ml-auto text-primary">Auto-creates a cleaning job per checkout</span>
      </div>
    </div>
  );
}

function JobUI() {
  return (
    <div className="p-5">
      <p className="text-xs font-semibold tracking-[0.12em] uppercase text-primary mb-3">
        Cleaning job
      </p>
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <p className="font-display font-semibold">Beach House turnover</p>
          <span className="text-[10px] rounded-full bg-secondary text-secondary-foreground px-2 py-0.5">
            Checkout 11am
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5">Door code 4827 · 3 bed / 2 bath</p>
        <div className="mt-3 space-y-1.5">
          {["Strip & wash all linens", "Restock toiletries & coffee", "Stage for next guest"].map((t, i) => (
            <div key={t} className="flex items-center gap-2 text-xs">
              <span className={`h-4 w-4 rounded-md flex items-center justify-center ${i < 2 ? "bg-primary text-primary-foreground" : "border border-border"}`}>
                {i < 2 && <Check className="h-3 w-3" />}
              </span>
              {t}
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-border flex items-center gap-2">
          <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[11px] font-semibold">
            M
          </div>
          <div className="text-[11px]">
            <p className="font-medium">Maria G.</p>
            <p className="text-muted-foreground">Assigned · $65 payout</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhotosUI() {
  return (
    <div className="p-5">
      <p className="text-xs font-semibold tracking-[0.12em] uppercase text-primary mb-3">
        Photo proof on completion
      </p>
      <div className="grid grid-cols-3 gap-2">
        {PHOTOS.map((src, i) => (
          <div key={i} className="relative rounded-lg overflow-hidden border border-border aspect-square">
            <img src={src} alt="after-service" className="h-full w-full object-cover" />
            <span className="absolute bottom-1 left-1 text-[9px] bg-black/55 text-white rounded px-1 py-0.5">
              {["Living", "Bath", "Bedroom"][i]}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-lg bg-secondary/60 p-2.5 text-[11px] flex items-center gap-2">
        <Camera className="h-4 w-4 text-primary" />
        After-service & damage photos timestamped for every job.
      </div>
    </div>
  );
}

function InvoiceUI() {
  const lines = [
    ["Turnover clean — Beach House", "$165"],
    ["Linen laundry (2 loads)", "$40"],
    ["Restock supplies", "$25"],
  ];
  return (
    <div className="p-5">
      <p className="text-xs font-semibold tracking-[0.12em] uppercase text-primary mb-3">
        Homeowner invoice
      </p>
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-display font-semibold">INV-0042</p>
            <p className="text-[11px] text-muted-foreground">Due Jun 30</p>
          </div>
          <span className="text-[10px] rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5">Paid</span>
        </div>
        <div className="mt-3 space-y-1.5">
          {lines.map(([l, v]) => (
            <div key={l} className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{l}</span>
              <span>{v}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
          <span className="text-xs font-medium">Total</span>
          <span className="font-display text-xl font-semibold">$230</span>
        </div>
      </div>
      <div className="mt-3 text-[11px] text-muted-foreground flex items-center gap-2">
        <FileText className="h-4 w-4 text-primary" /> Branded PDF · auto reminders · late fees
      </div>
    </div>
  );
}

function PayoutUI() {
  const rows = [
    ["Maria G.", "9 jobs", "$585"],
    ["Devon P.", "7 jobs", "$455"],
    ["Aisha K.", "5 jobs", "$325"],
  ];
  return (
    <div className="p-5">
      <p className="text-xs font-semibold tracking-[0.12em] uppercase text-primary mb-3">
        Cleaner payouts
      </p>
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {rows.map(([name, jobs, amt], i) => (
          <div key={name} className="flex items-center gap-3 p-3 border-t border-border first:border-0">
            <div className="h-8 w-8 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-[11px] font-semibold">
              {name[0]}
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium">{name}</p>
              <p className="text-[10px] text-muted-foreground">{jobs}</p>
            </div>
            <span className="font-display font-semibold text-sm">{amt}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between rounded-lg bg-primary text-primary-foreground p-3">
        <span className="text-xs">Week total</span>
        <span className="font-display text-lg font-semibold">$1,365</span>
      </div>
    </div>
  );
}

const SLIDES = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, caption: "Run your whole operation from one screen — turnovers, money, and team at a glance.", render: <DashboardUI /> },
  { id: "calendar", label: "iCal sync", icon: CalendarDays, caption: "Connect Airbnb & VRBO calendars. A cleaning job is created automatically for every checkout.", render: <CalendarUI /> },
  { id: "job", label: "Cleaning jobs", icon: ClipboardList, caption: "Dispatch cleaners with door codes, checklists and a clear payout — no group texts.", render: <JobUI /> },
  { id: "photos", label: "Photo proof", icon: Camera, caption: "Cleaners upload after-service and damage photos, timestamped on every job.", render: <PhotosUI /> },
  { id: "invoice", label: "Invoicing", icon: FileText, caption: "Send branded invoices to home owners with automatic reminders and late fees.", render: <InvoiceUI /> },
  { id: "payouts", label: "Payouts", icon: Wallet, caption: "Auto-split pay across the team and export CSV or branded PDF statements.", render: <PayoutUI /> },
];

const DURATION = 4200;

function DemoTour() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const go = useCallback((i) => setActive(((i % SLIDES.length) + SLIDES.length) % SLIDES.length), []);

  useEffect(() => {
    if (paused) return undefined;
    timer.current = setTimeout(() => setActive((a) => (a + 1) % SLIDES.length), DURATION);
    return () => clearTimeout(timer.current);
  }, [active, paused]);

  const current = SLIDES[active];

  return (
    <div
      className="grid lg:grid-cols-[1.4fr_1fr] gap-6 items-center"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="demo-tour"
    >
      {/* Browser frame */}
      <div className="rounded-2xl border border-border bg-card shadow-xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 h-10 border-b border-border bg-muted/50">
          <span className="h-3 w-3 rounded-full bg-rose-400/70" />
          <span className="h-3 w-3 rounded-full bg-amber-400/70" />
          <span className="h-3 w-3 rounded-full bg-emerald-400/70" />
          <div className="mx-auto text-[11px] text-muted-foreground bg-background border border-border rounded-full px-3 py-0.5">
            app.smarthostsolutions.org
          </div>
        </div>
        <div className="relative h-[420px] bg-background overflow-hidden">
          {SLIDES.map((s, i) => (
            <div
              key={s.id}
              className="absolute inset-0 transition-all duration-700 ease-out"
              style={{
                opacity: i === active ? 1 : 0,
                transform: i === active ? "translateY(0)" : "translateY(12px)",
                pointerEvents: i === active ? "auto" : "none",
              }}
              data-testid={i === active ? "demo-active-slide" : undefined}
            >
              {s.render}
            </div>
          ))}
        </div>
      </div>

      {/* Caption + controls */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary text-secondary-foreground text-xs px-3 py-1">
            <current.icon className="h-3.5 w-3.5 text-primary" /> {current.label}
          </span>
          <button
            onClick={() => setPaused((p) => !p)}
            className="ml-auto h-8 w-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground"
            data-testid="demo-playpause"
            aria-label={paused ? "Play" : "Pause"}
          >
            {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </button>
        </div>
        <p className="font-display text-xl font-semibold leading-snug min-h-[3.5rem]" data-testid="demo-caption">
          {current.caption}
        </p>

        {/* Chapter list */}
        <div className="mt-5 space-y-1">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              onClick={() => go(i)}
              data-testid={`demo-chapter-${s.id}`}
              className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                i === active ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-muted"
              }`}
            >
              <s.icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
              <span className="flex-1 text-left">{s.label}</span>
              {i === active && (
                <span className="relative h-1 w-12 rounded-full bg-border overflow-hidden">
                  <span
                    key={active + (paused ? "-p" : "")}
                    className="absolute inset-y-0 left-0 bg-primary rounded-full"
                    style={{ animation: paused ? "none" : `demoBar ${DURATION}ms linear forwards` }}
                  />
                </span>
              )}
            </button>
          ))}
        </div>

        <Link to="/signup" className="block mt-6">
          <Button className="w-full h-11 text-base" data-testid="demo-cta">
            Start free trial <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
      </div>

      <style>{`@keyframes demoBar { from { width: 0% } to { width: 100% } }`}</style>
    </div>
  );
}

const features = [
  { icon: CalendarDays, title: "Airbnb & VRBO sync", desc: "Auto-create turnover jobs from your iCal calendars." },
  { icon: Camera, title: "Photo proof", desc: "After-service & damage photos, per-room checklists." },
  { icon: Wallet, title: "Cleaner payouts", desc: "Auto-split pay, CSV & branded PDF statements." },
];

export default function Demo() {
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="SmartHost" className="h-9 w-9 object-contain" />
            <span className="font-display text-xl font-bold">SmartHost Solutions</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" data-testid="nav-login">Sign in</Button>
            </Link>
            <Link to="/signup">
              <Button data-testid="nav-signup">Start free trial</Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-1.5 text-sm text-secondary-foreground mb-6">
          <Sparkles className="h-4 w-4" /> Product tour
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] max-w-3xl mx-auto">
          See SmartHost in action.
        </h1>
        <p className="text-base md:text-lg text-muted-foreground mt-5 max-w-2xl mx-auto">
          A 30-second walkthrough of how cleaning companies run every turnover — from synced
          calendars to spotless handoffs and paid invoices.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16">
        <DemoTour />
      </section>

      {/* Feature highlights */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid sm:grid-cols-3 gap-4">
          {features.map((f) => (
            <Card key={f.title} className="p-6">
              <f.icon className="h-6 w-6 text-primary mb-3" strokeWidth={1.7} />
              <h3 className="font-display font-semibold text-lg">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <Card className="p-10 text-center bg-primary text-primary-foreground border-0">
          <h2 className="font-display text-3xl font-semibold">Ready to try it on your properties?</h2>
          <p className="opacity-90 mt-2">Free 3-day trial · from $39/mo · cancel anytime.</p>
          <Link to="/signup" className="inline-block mt-6">
            <Button size="lg" variant="secondary" className="h-12 px-8 text-base" data-testid="demo-bottom-cta">
              Start free trial
            </Button>
          </Link>
        </Card>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        © {new Date().getFullYear()} SmartHost Solutions · Smart Systems. Seamless Stays.
      </footer>
    </div>
  );
}
